/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, ChangeEvent, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { sys } from '@netbasequid/theme'
// @ts-ignore
import { TextField, Button, QLogo } from '@netbasequid/core'
import SystemMessageLayout from 'components/wrapper/SystemMessageLayout'
import { setAutoId, AutomationId } from 'utils/automationUtil'
import { ErrorCodes } from 'liveReportService'
import { resetLoginState } from 'features/liveReport/liveReportSlice'
import { useAppDispatch } from 'app/hooks'
import { ErrorCodeToTranslationKey } from '../utils'
import { useLiveReportAuth } from '../hooks'

const { dark, light } = sys

const Title = styled.div`
  font-family: var(--large-font-family);
  font-weight: ${sys.fontWeight.regular};
  font-size: 36px;
  line-height: 42px;
  margin-bottom: 40px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px 40px;
`

const Label = styled.label`
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
  font-weight: ${sys.fontWeight.regular};
  color: ${dark.text.weak};
  text-align: left;
  & input {
    margin-top: 4px;
    margin-bottom: 32px;
  }
`

const ErrorMessage = styled.div`
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
  font-weight: ${sys.fontWeight.regular};
  color: ${dark.semantic.negative.default};
  text-align: left;
  margin-bottom: 16px;
`

const StyledButton = styled(Button)`
  margin-top: 8px;
`

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const Spinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${rotateAnimation} 1s linear infinite;
`

const LoginErrorCodes = [ErrorCodes.InvalidEmailOrReportKey, ErrorCodes.UserNotActive]

const Login = () => {
  const [email, setEmail] = useState('')
  const [reportKey, setReportKey] = useState('')
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    login: { status, error },
    handleLogin
  } = useLiveReportAuth()
  const errorCode = error?.status
  const isLoggingIn = status === 'loading'
  const canSubmitForm = email && reportKey && !isLoggingIn
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSubmitForm) {
      handleLogin({ email, reportKey })
    }
  }
  useEffect(() => {
    dispatch(resetLoginState())
  }, [dispatch])

  return (
    <SystemMessageLayout>
      <Title {...setAutoId(AutomationId.liveReport.login.title)}>{t('liveReportLogin')}</Title>
      <Content>
        {errorCode && (
          <ErrorMessage {...setAutoId(AutomationId.liveReport.login.errorMessage)}>
            {LoginErrorCodes.includes(errorCode)
              ? t(ErrorCodeToTranslationKey[errorCode]!)
              : t('unexpectedError')}
          </ErrorMessage>
        )}
        <Label {...setAutoId(AutomationId.liveReport.login.email)}>
          {t('email')}
          <TextField
            mode="dark"
            fullWidth
            placeholder=""
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value)
            }}
            onKeyDown={handleKeyDown}
          />
        </Label>
        <Label {...setAutoId(AutomationId.liveReport.login.reportKey)}>
          {t('reportKey')}
          <TextField
            mode="dark"
            fullWidth
            placeholder=""
            value={reportKey}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setReportKey(e.target.value)
            }}
            onKeyDown={handleKeyDown}
          />
        </Label>
        <StyledButton
          mode="dark"
          disabled={!canSubmitForm}
          onClick={() => handleLogin({ email, reportKey })}
          {...setAutoId(AutomationId.liveReport.login.button)}>
          {isLoggingIn ? (
            <Spinner>
              <QLogo fontSize={16} color={light.text.primary} />
            </Spinner>
          ) : (
            t('logIn')
          )}
        </StyledButton>
      </Content>
    </SystemMessageLayout>
  )
}

export default Login
